import React, { useEffect, useRef, useState } from 'react'
import { Table, Button, Badge, Modal, Form } from 'react-bootstrap'
import { toast } from 'react-toastify'
import {
  getPendingWeekendWorkRequests,
  approveWeekendWorkRequest,
  rejectWeekendWorkRequest,
} from '../../../api/WeekendWorkAPI'
import { useAuth } from '../../modules/auth'
import ListPagination from '../../components/ListPagination'

const CompOffApproval: React.FC = () => {
  const { currentUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [requests, setRequests] = useState<any[]>([])
  const [pagination, setPagination] = useState({page: 1, pageSize: 10, total: 0, totalPages: 0})
  const requestVersion = useRef(0)

  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [rejectionError, setRejectionError] = useState('')

  // ===============================
  // LOAD REQUESTS
  // ===============================
  const loadRequests = async (page = pagination.page, pageSize = pagination.pageSize) => {
    if (!currentUser?.id) return
    const version = ++requestVersion.current

    try {
      setLoading(true)

      // Admin / HR / RM → backend decides scope
      const res = await getPendingWeekendWorkRequests({
        status: 'PENDING',
        page,
        pageSize,
      })
      if (requestVersion.current !== version) return

      // setRequests(res.data?.data?.requests || [])
      const requests = res.data?.data?.requests || []
      // Sort by requestDate descending
      requests.sort((a: any, b: any) => new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime())
      setRequests(requests)
      if (res.data?.data?.pagination) {
        setPagination((previous) => ({...previous, ...res.data.data.pagination}))
      }

    } catch (error) {
      if (requestVersion.current === version) toast.error('Failed to load comp-off requests')
    } finally {
      if (requestVersion.current === version) setLoading(false)
    }
  }

  // ===============================
  // EFFECT (SAFE)
  // ===============================
  useEffect(() => {
    loadRequests()
    return () => {
      requestVersion.current += 1
    }
  }, [currentUser?.id])

  // ===============================
  // APPROVE
  // ===============================
  const handleApprove = async (requestId: string) => {
    if (!currentUser?.id) return

    try {
      await approveWeekendWorkRequest(requestId, {
        reviewedById: currentUser.id,
        compOffGranted: true,
        compOffHours: 6, // 6 hrs = 1 Comp-Off day
        compOffReason: 'Approved',
      })

      toast.success('Comp-Off approved successfully')
      loadRequests()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Approval failed'
      )
    }
  }

  // ===============================
  // REJECT - OPEN MODAL
  // ===============================
  const openRejectModal = (requestId: string) => {
    setSelectedRequestId(requestId)
    setRejectionReason('')
    setRejectionError('')
    setShowRejectModal(true)
  }

  // ===============================
  // REJECT - SUBMIT
  // ===============================
  const handleRejectSubmit = async () => {
    if (!currentUser?.id || !selectedRequestId) return
    // Validate minimum length
    const trimmedReason = rejectionReason.trim()
    if (trimmedReason.length < 10) {
      setRejectionError(`Rejection reason must be at least 10 characters (${10 - trimmedReason.length} more needed)`)
      return
    }

    try {
      await rejectWeekendWorkRequest(selectedRequestId, {
        reviewedById: currentUser.id,
        rejectionReason: trimmedReason,
      })

      toast.success('Request rejected successfully')
      setShowRejectModal(false)
      setSelectedRequestId(null)
      setRejectionReason('')
      loadRequests()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || 'Rejection failed'
      )
    }
  }
  // ===============================
  // EARLY SAFE RETURN (AFTER HOOKS)
  // ===============================
  if (!currentUser) {
    return <div className="p-4 text-center">Loading user...</div>
  }
  
  return (
    <>
      <div className='card mb-4' style={{borderRadius: '12px', border: '1px solid rgb(226, 236, 247)', boxShadow: 'rgba(0, 0, 0, 0.05) 0px 1px 3px', backgroundColor: 'rgb(255, 255, 255)'}}>
        <div className='card-header' style={{background: 'rgb(255, 255, 255)', padding: '12px 20px', minHeight: 'unset', display: 'flex', alignItems: 'center', borderBottom: '1px solid rgb(226, 236, 247)', borderRadius: '12px 12px 0 0'}}>
          <h5 className='mb-0' style={{color: 'rgb(18, 59, 115)', fontSize: '16px', fontWeight: '600'}}>Comp-Off Approval</h5>
        </div>

        <div className='card-body' style={{padding: '0px'}}>
          {loading ? (
            <div style={{padding: '80px 24px', textAlign: 'center'}}>
              <div style={{color: 'rgb(100, 116, 139)', fontSize: '14px'}}>Loading...</div>
            </div>
          ) : (
            <>
              <div style={{overflowX: 'auto', maxWidth: '100%'}}>
                <table style={{width: '100%', minWidth: '800px', tableLayout: 'fixed', borderCollapse: 'collapse', backgroundColor: 'rgb(255, 255, 255)'}}>
                  <thead>
                    <tr style={{backgroundColor: 'rgb(244, 248, 253)'}}>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '20%'}}>Employee</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '12%'}}>Date</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '10%'}}>Hours</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '25%'}}>Reason</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '12%'}}>Status</th>
                      <th style={{padding: '12px 16px', textAlign: 'left', color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', borderBottom: '1px solid rgb(226, 236, 247)', whiteSpace: 'nowrap', width: '21%'}}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {requests.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{padding: '80px 24px', textAlign: 'center'}}>
                          <div style={{backgroundColor: 'rgb(244, 248, 253)', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0px auto 20px'}}>
                            <i className='fa fa-clock fa-2x' style={{color: 'rgb(100, 116, 139)', fontSize: '32px'}}></i>
                          </div>
                          <div style={{color: 'rgb(18, 59, 115)', fontSize: '18px', fontWeight: '600', marginBottom: '8px'}}>No pending requests</div>
                          <div style={{color: 'rgb(100, 116, 139)', fontSize: '14px'}}>All comp-off requests have been processed</div>
                        </td>
                      </tr>
                    )}

                    {requests.map((req) => (
                      <tr key={req.id} style={{borderBottom: '1px solid rgb(226, 236, 247)', transition: 'background-color 0.2s', backgroundColor: 'transparent'}}>
                        <td style={{padding: '12px 16px', verticalAlign: 'middle'}}>
                          <div style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', marginBottom: '4px'}}>
                            {req.user?.employeeCode}
                          </div>
                          <div style={{color: 'rgb(100, 116, 139)', fontSize: '11px'}}>
                            {req.user?.firstName} {req.user?.lastName}
                          </div>
                        </td>

                        <td style={{padding: '12px 16px', color: 'rgb(100, 116, 139)', fontSize: '12px', verticalAlign: 'middle'}}>
                          {new Date(req.requestDate).toLocaleDateString()}
                        </td>

                        <td style={{padding: '12px 16px', color: 'rgb(100, 116, 139)', fontSize: '12px', verticalAlign: 'middle'}}>
                          {req.estimatedHours ?? '-'}
                        </td>

                        <td style={{padding: '12px 16px', color: 'rgb(100, 116, 139)', fontSize: '12px', verticalAlign: 'middle'}}>
                          {req.reason}
                        </td>

                        <td style={{padding: '12px 16px', verticalAlign: 'middle'}}>
                          {req.status === 'PENDING' && (
                            <span style={{backgroundColor: 'rgb(255, 251, 235)', color: 'rgb(217, 119, 6)', padding: '4px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: '600', display: 'inline-block', whiteSpace: 'nowrap'}}>PENDING</span>
                          )}
                          {req.status === 'APPROVED' && (
                            <span style={{backgroundColor: 'rgb(240, 253, 244)', color: 'rgb(22, 163, 74)', padding: '4px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: '600', display: 'inline-block', whiteSpace: 'nowrap'}}>APPROVED</span>
                          )}
                          {req.status === 'REJECTED' && (
                            <span style={{backgroundColor: 'rgb(254, 242, 242)', color: 'rgb(239, 68, 68)', padding: '4px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: '600', display: 'inline-block', whiteSpace: 'nowrap'}}>REJECTED</span>
                          )}
                        </td>

                        <td style={{padding: '12px 16px', verticalAlign: 'middle'}}>
                          {req.status === 'PENDING' ? (
                            <div style={{display: 'flex', gap: '8px'}}>
                              <Button
                                size="sm"
                                style={{
                                  backgroundColor: 'rgb(22, 163, 74)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  cursor: 'pointer'
                                }}
                                onClick={() => handleApprove(req.id)}
                              >
                                Approve
                              </Button>

                              <Button
                                size="sm"
                                style={{
                                  backgroundColor: 'rgb(239, 68, 68)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  cursor: 'pointer'
                                }}
                                onClick={() => openRejectModal(req.id)}
                              >
                                Reject
                              </Button>
                            </div>
                          ) : req.status === 'REJECTED' ? (
                            <span style={{color: 'rgb(239, 68, 68)', fontSize: '11px', fontStyle: 'italic'}}>
                              {req.rejectionReason || '—'}
                            </span>
                          ) : (
                            <span style={{color: 'rgb(148, 163, 184)', fontSize: '12px'}}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className='px-4 py-3'>
                <ListPagination
                  page={pagination.page}
                  pageSize={pagination.pageSize}
                  totalRecords={pagination.total}
                  disabled={loading}
                  onPageChange={(page) => loadRequests(page)}
                  onPageSizeChange={(pageSize) => loadRequests(1, pageSize)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Rejection Reason Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered style={{borderRadius: '12px'}}>
        <Modal.Header closeButton style={{borderBottom: '1px solid rgb(226, 236, 247)', padding: '16px 20px'}}>
          <Modal.Title style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '16px'}}>Reject Comp-Off Request</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{padding: '20px'}}>
          <Form.Group>
            <Form.Label style={{color: 'rgb(18, 59, 115)', fontWeight: '600', fontSize: '12px', marginBottom: '8px'}}>
              Rejection Reason <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter reason for rejection (minimum 10 characters)"
              value={rejectionReason}
              onChange={(e) => {
                setRejectionReason(e.target.value)
                setRejectionError('')
              }}
              isInvalid={!!rejectionError}
              style={{
                borderRadius: '8px',
                border: '1px solid rgb(226, 236, 247)',
                fontSize: '12px'
              }}
            />
            <Form.Control.Feedback type="invalid">
              {rejectionError}
            </Form.Control.Feedback>
            {rejectionReason.trim().length > 0 && rejectionReason.trim().length < 10 && (
              <Form.Text className="text-warning" style={{fontSize: '11px'}}>
                {10 - rejectionReason.trim().length} more character(s) required
              </Form.Text>
            )}
            {rejectionReason.trim().length >= 10 && (
              <Form.Text className="text-success" style={{fontSize: '11px'}}>
                ✓ Valid rejection reason
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{borderTop: '1px solid rgb(226, 236, 247)', padding: '16px 20px'}}>
          <Button 
            variant="secondary" 
            onClick={() => setShowRejectModal(false)}
            style={{
              backgroundColor: 'rgb(248, 250, 252)',
              color: 'rgb(100, 116, 139)',
              border: '1px solid rgb(226, 236, 247)',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRejectSubmit}
            disabled={rejectionReason.trim().length < 10}
            style={{
              backgroundColor: rejectionReason.trim().length < 10 ? 'rgb(203, 213, 225)' : 'rgb(239, 68, 68)',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: rejectionReason.trim().length < 10 ? 'not-allowed' : 'pointer'
            }}
          >
            Reject Request
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default CompOffApproval

